import { ReactElement, useId } from 'react';
import { Cart } from '@client/cart/components/Cart';
import { isEmpty } from '@client/common/utils/empty/empty';
import { useTranslate } from '@client/i18n/hooks';
import { WishlistModal } from '@client/myAccount/wishlists/components/WishlistModal';
import { Alert, ALERT_DISPLAY_TYPE, ALERT_STYLE_TYPE } from 'b2b-components/theme/components/Alert';
import { Button, BUTTON_ICON_POSITION, BUTTON_SIZE, BUTTON_STYLE_TYPE } from 'b2b-components/theme/components/Button';
import { CardBox } from 'b2b-components/theme/components/CardBox';
import { DATA_PAIR_DIRECTION, DataPair } from 'b2b-components/theme/components/DataPair';
import { STYLE_TYPE, TextArea } from 'b2b-components/theme/components/Form/TextArea';
import { Loader, LOADER_STYLE_TYPE } from 'b2b-components/theme/components/Loader';
import { PriceSummary, PriceSummaryType } from 'b2b-components/theme/components/PriceSummary';
import { CLONE_FORM_MODE } from 'b2b-apps/core/myAccount/wishlists/components/Forms/CloneForm';
import { Props } from './StandaloneViewPage.types';
import './StandaloneViewPage.scss';

export const StandaloneViewPage = ({
  hasAccessCartPermissions,
  isAddItemsToCartDisabled,
  isModalOpened,
  modalType,
  onAddItemsToCartClick,
  onCloneClick,
  onModalCloseClick,
  onSelectedItemsChange,
  selectedItems,
  wishlist,
  onAddItemsToCart,
  onRemoveItems,
}: Props): ReactElement | null => {
  const t = useTranslate();
  const { data, isLoading } = wishlist;
  const areaId = useId();

  const priceSummaryData = (): PriceSummaryType => {
    const priceSummaryData: PriceSummaryType = [];

    if (!data) {
      return priceSummaryData;
    }

    priceSummaryData.push({
      label: t('Total (excl. VAT)'),
      value: data.totalPriceWithoutVat,
    });

    priceSummaryData.push({
      label: t('VAT'),
      value: data.totalPriceWithVat - data.totalPriceWithoutVat,
    });

    priceSummaryData.push({
      isTotal: true,
      label: t('Total (incl. VAT)'),
      value: data.totalPriceWithVat,
    });

    return priceSummaryData;
  };

  const renderProductListActions = (): ReactElement => (
    <>
      {hasAccessCartPermissions && (
        <Button
          className="WishlistDetailsPage-ItemsActionButton"
          isDisabled={isAddItemsToCartDisabled}
          onClick={onAddItemsToCartClick}
          size={BUTTON_SIZE.small}
          styleType={BUTTON_STYLE_TYPE.primary}
        >
          {selectedItems.length > 0
            ? t('Add selected items to cart')
            : t('Add all items to cart')}
        </Button>
      )}
    </>
  );

  if (!data) {
    return null;
  }

  return (
    <>
      <Alert className="MyAccount-Alert" styleType={ALERT_STYLE_TYPE.info}>
        <span
          dangerouslySetInnerHTML={{
            __html: t(
              'User {email} has shared a wishlist with you. You can save a copy to your account or reload this URL to see changes made by the owner.',
              { email: data && data.username },
            ),
          }}
        />
      </Alert>

      <CardBox className="WishlistDetailsPage-ContentBox">
        {isLoading && <Loader styleType={LOADER_STYLE_TYPE.fill} />}

        <div className="WishlistDetailsPage-Header">
          <div className="WishlistDetailsPage-DetailsWrapper">
            <div>
              <div className="WishlistDetailsPage-TitleWrapper">
                <h3 className='WishlistDetailsPage-TitleText' data-testid='wishlistTitle'>{data.name}</h3>
              </div>

              <p className="WishlistDetailsPage-WishlistIdText">
                {t('(Wishlist ID: {id})', { id: data && data.id })}
              </p>

              <TextArea
                textareaId={areaId}
                className="WishlistDetailsPage-ReferenceTextArea"
                defaultValue={data.reference || ''}
                isDisabled
                rows={1}
                styleType={STYLE_TYPE.underlinedFloatingLabel}
                text={{ label: t('Reference') }}
              />
            </div>

            <div>
              <div className="WishlistDetailsPage-Dates">
                <DataPair
                  direction={DATA_PAIR_DIRECTION.column}
                  label={t('Created')}
                >
                  {data.createdAt}
                </DataPair>

                <DataPair
                  direction={DATA_PAIR_DIRECTION.column}
                  label={t('Last updated')}
                >
                  {data.updatedAt}
                </DataPair>
              </div>

              <PriceSummary
                className="MyAccount-PriceSummary"
                data={priceSummaryData()}
              />
            </div>
          </div>

          <div className="WishlistDetailsPage-ActionButtons">
            <Button
              className="WishlistDetailsPage-ActionButton"
              iconPosition={BUTTON_ICON_POSITION.left}
              onClick={onCloneClick}
              size={BUTTON_SIZE.small}
              styleType={BUTTON_STYLE_TYPE.light}
            >
              {t('Save a copy to MyAccount')}
            </Button>
          </div>
        </div>

        {isEmpty(data.items)
          ? (
            <Alert
              displayType={ALERT_DISPLAY_TYPE.text}
              styleType={ALERT_STYLE_TYPE.info}
            >
              {t('Your wishlist is currently empty.')}
            </Alert>
          )
          : (
            <>
              <div className="WishlistDetailsPage-ItemsActionButtons">
                {renderProductListActions()}
              </div>

              <Cart
                products={data.items}
                hasActions
                onSelectedProductsChange={onSelectedItemsChange}
                selectedProducts={selectedItems}
                className="WishlistDetailsPage-ProductList"
              />

              <div className="WishlistDetailsPage-ItemsActionButtons WishlistDetailsPage-ItemsActionButtons--Bottom">
                {renderProductListActions()}
              </div>
            </>
          )
        }
      </CardBox>

      <WishlistModal
        wishlistId={data.id}
        wishlistName={data.name}
        type={modalType}
        formMode={CLONE_FORM_MODE.copy}
        isOpen={isModalOpened}
        areAllProductsSelected={selectedItems.length === 0}
        onAddItemsToCart={onAddItemsToCart}
        onRemoveItems={onRemoveItems}
        onCloseModal={onModalCloseClick}
      />
    </>
  );
};
